import React, { useState, useRef, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { PhotoCamera, Delete, Close } from '@mui/icons-material';
import styled from 'styled-components';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';

/**
 * Image compression utility (reused from File.tsx)
 */
async function compressImage(file: File, maxWidth: number = 1920, maxHeight: number = 1920, quality: number = 0.8): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        
        const outputType = file.type === 'image/png' ? 'image/jpeg' : file.type;
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Compression failed'));
              return;
            }
            const compressedFile = new window.File([blob], file.name, {
              type: outputType,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          outputType,
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  margin: 16px 0;
  border: 1px solid ${props => props.theme.palette.divider};
  border-radius: 4px;
`;

const FileInputLabel = styled.label`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export interface SignatureUploadProps extends LocalizeContextProps {
  /** Current signature base64 string (if exists) */
  currentSignature?: string | null;
  
  /** Callback when user uploads a file */
  onUpload: (file: File) => Promise<void>;
  
  /** Callback when user removes signature */
  onRemove?: () => Promise<void>;
  
  /** Whether upload is in progress */
  uploading?: boolean;
  
  /** Error message to display */
  error?: string | null;
  
  /** Whether component is disabled */
  disabled?: boolean;
}

/**
 * SignatureUpload Component
 * 
 * Allows users to upload their signature image (PNG/JPG).
 * Features:
 * - Image preview before upload
 * - Automatic compression for large images
 * - File type and size validation
 * - Loading and error states
 */
const SignatureUploadComponent: React.FC<SignatureUploadProps> = ({
  currentSignature,
  onUpload,
  onRemove,
  uploading = false,
  error = null,
  disabled = false,
  translate,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Max file sizes
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB hard limit
  const WARN_FILE_SIZE = 5 * 1024 * 1024; // 5MB warning

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset previous states
    setValidationError(null);
    setSelectedFile(null);
    setPreviewUrl(null);

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setValidationError(translate('profile.signature.errors.invalid_type') as string);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      const sizeText = `${translate('profile.signature.errors.too_large')} (${(file.size / 1024 / 1024).toFixed(1)}MB). ${translate('profile.signature.errors.max_size')}`;
      setValidationError(sizeText as string);
      return;
    }

    // Warn about large files and compress
    let processedFile = file;
    if (file.size > WARN_FILE_SIZE) {
      try {
        setCompressing(true);
        const originalSize = file.size;
        processedFile = await compressImage(file, 1920, 1920, 0.85);
        const compressedSize = processedFile.size;
        const reduction = ((1 - compressedSize / originalSize) * 100).toFixed(0);
        console.log(`Image compressed: ${(originalSize / 1024 / 1024).toFixed(2)}MB → ${(compressedSize / 1024 / 1024).toFixed(2)}MB (${reduction}% reduction)`);
      } catch (err) {
        console.error('Compression failed:', err);
        // Continue with original file if compression fails
        processedFile = file;
      } finally {
        setCompressing(false);
      }
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(processedFile);

    setSelectedFile(processedFile);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await onUpload(selectedFile);
      // Clear selection after successful upload
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      // Error is handled by parent component
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (onRemove) {
      await onRemove();
    }
  };

  const hasCurrentSignature = currentSignature && !selectedFile;
  const hasNewSelection = selectedFile && previewUrl;
  const displayError = error || validationError;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          ✍️ <Translate id="profile.signature.title" />
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          <Translate id="profile.signature.description" />
        </Typography>

        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError}
          </Alert>
        )}

        {/* Current signature display */}
        {hasCurrentSignature && (
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <Translate id="profile.signature.current" />
            </Typography>
            <PreviewImage src={currentSignature!} alt="Current signature" />
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <FileInputLabel>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  disabled={disabled || uploading || compressing}
                />
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  disabled={disabled || uploading || compressing}
                >
                  <Translate id="profile.signature.change_signature" />
                </Button>
              </FileInputLabel>
              
              {onRemove && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleRemove}
                  disabled={disabled || uploading}
                >
                  <Translate id="profile.signature.remove" />
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* No signature yet */}
        {!hasCurrentSignature && !hasNewSelection && (
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <Translate id="profile.signature.no_signature" />
            </Typography>
            <FileInputLabel>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={disabled || uploading || compressing}
              />
              <Button
                variant="contained"
                component="span"
                startIcon={<PhotoCamera />}
                disabled={disabled || uploading || compressing}
                sx={{ mt: 2 }}
              >
                <Translate id="profile.signature.choose_file" />
              </Button>
            </FileInputLabel>
          </Box>
        )}

        {/* New selection preview */}
        {hasNewSelection && (
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <Translate id="profile.signature.preview" />
            </Typography>
            <PreviewImage src={previewUrl!} alt="Signature preview" />
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              {selectedFile!.name} ({(selectedFile!.size / 1024).toFixed(0)} KB)
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading || compressing || disabled}
                startIcon={uploading ? <CircularProgress size={20} /> : undefined}
              >
                {uploading ? <Translate id="profile.signature.uploading" /> : <Translate id="profile.signature.upload" />}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={uploading || compressing || disabled}
                startIcon={<Close />}
              >
                <Translate id="general.cancel" />
              </Button>
            </Box>
          </Box>
        )}

        {compressing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              <Translate id="profile.signature.compressing" />
            </Typography>
          </Box>
        )}

        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 2 }}>
          <Translate id="profile.signature.accepted_formats" />
        </Typography>
      </CardContent>
    </Card>
  );
};

export default withLocalize(SignatureUploadComponent);

